class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.requestIndex = 0;
    this.activeOption = -1;
    this.searchContent = this.querySelector('.search__content');
    this.status = this.querySelector('[data-search-status]');
    this.resultsElement = this.querySelector('[role="listbox"]');
    this.correctionElement = this.querySelector('[data-search-correction]');
    this.correction = null;
    this.exactQueries = new Set();
    // "Søk etter «…»" on the results page means exactly what was typed; keep it that way while editing.
    const pageParams = new URLSearchParams(window.location.search);
    if (this.closest('.template-search') && pageParams.has('exact')) this.exactQueries.add(this.queryKey(pageParams.get('q') || ''));
    this.menuDrawer = this.hasAttribute('data-menu-search-form') ? this.closest('menu-drawer') : null;
    if (this.menuDrawer) {
      this.querySelector('[data-menu-search-back]')?.addEventListener('click', () => this.exitMenuSearch());
      this.menuDrawer.addEventListener('keydown', this.onMenuKeyDown.bind(this), true);
      this.menuDrawer.addEventListener('keyup', (event) => {
        if (event.key !== 'Escape' || !this.menuDrawer.hasAttribute('data-search-active')) return;
        event.preventDefault();
        event.stopPropagation();
        this.exitMenuSearch();
      }, true);
      this.menuDrawer.addEventListener('drawer:handleAfterHide', (event) => {
        if (event.target === this.menuDrawer) this.exitMenuSearch(false);
      });
    }
    this.resetButton?.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', () => {
      if (this.getQuery()) this.enterMenuSearch();
      this.abortController?.abort();
      this.requestIndex += 1;
      this.resetButton.disabled = this.getQuery().length === 0;
      this.resetSelection();
      this.showCorrection(null);
      this.input.setAttribute('aria-expanded', 'false');
      this.removeAttribute('results');
      this.toggleAttribute('data-has-query', this.getQuery().length > 0);
      this.querySelector('.search__results-all').disabled = true;
      this.resultsElement.innerHTML = '';
      this.refreshDrawerFocus();
      if (this.getQuery().length === 0) this.clear();
      else this.debouncedChange();
    });
    this.debouncedChange = FoxTheme.utils.debounce(this.onChange.bind(this), 200);
    this.input.addEventListener('focus', this.onFocus.bind(this));
    // The menu turns into search on a tap or on typing, not on focus, so Tab still reaches the menu links.
    this.input.addEventListener('click', () => this.enterMenuSearch());
    this.input.addEventListener('keydown', this.onKeyDown.bind(this));
    this.addEventListener('submit', this.onSubmit.bind(this));
    this.addEventListener('focusout', () => {
      requestAnimationFrame(() => {
        if (!this.contains(document.activeElement)) this.closeSuggestions();
      });
    });
    this.handleOutside = (event) => { if (!this.contains(event.target)) this.closeSuggestions(); };
    if (this.closest('.template-search')) document.addEventListener('click', this.handleOutside);
  }
  disconnectedCallback() {
    this.abortController?.abort();
    document.removeEventListener('click', this.handleOutside);
  }
  get input() { return this.querySelector('input[type="search"]'); }
  get resetButton() { return this.querySelector('button[type="reset"]'); }
  getQuery() { return this.input.value.trim(); }
  queryKey(query) {
    return String(query || '').toLocaleLowerCase('nb-NO').replace(/æ/g, 'ae').replace(/ø/g, 'o').normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '').replace(/[^\p{L}\p{N}\s]/gu, ' ').replace(/\s+/g, ' ').trim();
  }
  onSubmit(event) {
    const query = this.getQuery();
    if (!query) {
      event.preventDefault();
      this.input.focus();
      // «Søk» or Enter is an explicit choice, so the menu turns into search here too (focus alone does not).
      this.enterMenuSearch();
      return;
    }
    // Full results follow the query the suggestions used, and remember what was typed.
    const corrected = this.correction?.original === query;
    const exact = this.exactQueries.has(this.queryKey(query));
    if (!corrected && !exact) return;
    event.preventDefault();
    const url = new URL(this.getAttribute('action') || '/search', window.location.origin);
    url.searchParams.set('q', corrected ? this.correction.query : query);
    url.searchParams.set('options[prefix]', 'last');
    if (corrected) url.searchParams.set('oq', query);
    else url.searchParams.set('exact', '1');
    window.location.assign(url.toString());
  }
  onFocus() {
    window.LaserlabSearchTerms?.load();
    if (this.closest('.template-search')) document.body.classList.add('predictive-search-open');
    if (this.getQuery()) this.onChange();
  }
  enterMenuSearch() {
    if (!this.menuDrawer?.open || this.menuDrawer.hasAttribute('data-search-active')) return;
    this.menuDrawer.setAttribute('data-search-active', '');
    const content = this.menuDrawer.querySelector('.menu-drawer__content');
    content.hidden = true;
    content.setAttribute('inert', '');
    // Keep the same panel and focused input. No modal transition or keyboard delay.
    FoxTheme.a11y.removeTrapFocus();
  }
  exitMenuSearch(restoreFocus = true) {
    if (!this.menuDrawer) return;
    this.clear();
    this.menuDrawer.removeAttribute('data-search-active');
    const content = this.menuDrawer.querySelector('.menu-drawer__content');
    content.hidden = false;
    content.removeAttribute('inert');
    if (restoreFocus && this.menuDrawer.open) {
      const activeSubmenu = Array.from(content.querySelectorAll('details[is="menu-drawer-details"][open].active')).pop();
      const destination = activeSubmenu?.querySelector('.menu-drawer__item-back-link') || this.visibleFocusables(content)[0] || this.menuDrawer.querySelector('.drawer__close');
      FoxTheme.a11y.trapFocus(this.menuDrawer, destination);
    }
  }
  visibleFocusables(container) {
    return FoxTheme.a11y.getFocusableElements(container).filter(element =>
      !element.closest('[hidden], [inert]') && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden'
    );
  }
  onMenuKeyDown(event) {
    if (event.key !== 'Tab' || !this.menuDrawer.open || !this.menuDrawer.hasAttribute('data-search-active')) return;
    // The native trap includes hidden navigation links; use only the current search controls.
    const controls = this.visibleFocusables(this.menuDrawer);
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && event.target === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && event.target === last) {
      event.preventDefault();
      first?.focus();
    }
  }
  clear(event) {
    event?.preventDefault();
    this.abortController?.abort();
    this.requestIndex += 1;
    this.input.value = '';
    this.resetButton.disabled = true;
    this.removeAttribute('results');
    this.removeAttribute('data-has-query');
    this.querySelector('.search__results-all').disabled = true;
    this.removeAttribute('loading');
    this.removeAttribute('error');
    this.resetButton.classList.remove('btn--loading');
    this.resultsElement.innerHTML = '';
    this.resultsElement.removeAttribute('aria-busy');
    this.resetSelection();
    this.showCorrection(null);
    this.input.setAttribute('aria-expanded', 'false');
    this.status.textContent = '';
    if (event) this.input.focus();
    this.refreshDrawerFocus();
  }
  setupURL(query = this.getQuery()) {
    const url = new URL(FoxTheme.routes.predictive_search_url, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product,collection,page,article,query');
    url.searchParams.set('resources[limit]', Math.min(10, Number(this.dataset.resultsLimit) || 10));
    url.searchParams.set('resources[limit_scope]', 'all');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag');
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url.toString();
  }
  onChange() {
    if (!this.getQuery()) return;
    if (this.closest('.template-search')) document.body.classList.add('predictive-search-open');
    this.renderSection();
  }
  async fetchResults(query, signal) {
    const url = this.setupURL(query);
    let responseText = this.cachedMap.get(url);
    if (!responseText) {
      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error('Search unavailable');
      responseText = await response.text();
      this.cachedMap.set(url, responseText);
    }
    const doc = new DOMParser().parseFromString(responseText, 'text/html');
    const source = doc.getElementById(this.resultsElement.id);
    if (!source) throw new Error('Search results missing');
    this.cleanResults(source);
    return { source, count: source.querySelectorAll('[data-search-option]').length };
  }
  // One row per destination. Product rows win over a plain link to the same page, and equal labels appear once.
  cleanResults(source) {
    const normalize = (value) => this.queryKey(value.replace(/→/g, ''));
    const linkKey = (option) => {
      const href = option.querySelector('a')?.getAttribute('href');
      if (!href) return null;
      const url = new URL(href, window.location.origin);
      Array.from(url.searchParams.keys()).filter((name) => name.startsWith('_')).forEach((name) => url.searchParams.delete(name));
      return `link:${url.pathname.replace(/\/$/, '').toLowerCase()}${url.search}`;
    };
    const options = Array.from(source.querySelectorAll('[data-search-option]'));
    const rows = options.filter((option) => option.querySelector('.ll-search-result'));
    const links = options.filter((option) => !option.querySelector('.ll-search-result'));
    const seen = new Set();
    [...rows, ...links].forEach((option) => {
      const keys = [linkKey(option)];
      if (!option.querySelector('.ll-search-result')) keys.push(`label:${normalize(option.textContent)}`);
      if (keys.some((key) => key && seen.has(key))) option.remove();
      else keys.forEach((key) => key && seen.add(key));
    });
    source.querySelectorAll('.search__box-item').forEach((group) => {
      if (!group.querySelector('[data-search-option]')) group.remove();
    });
    // The category shortcuts and the collection hits are both «Kategorier»: show them as one group.
    const shortcutList = source.querySelector('.ll-search-category-matches ul');
    const collectionGroup = source.querySelector('[id$="-collections-heading"]')?.closest('.search__box-item');
    if (shortcutList && collectionGroup) {
      collectionGroup.querySelectorAll('[data-search-option]').forEach((option) => shortcutList.append(option));
      collectionGroup.remove();
    }
  }
  // Suggested results first, then anything only the typed query found, in the same groups.
  mergeResults(primary, secondary) {
    const target = primary.source;
    secondary.source.querySelectorAll('.search__box-item').forEach((group) => {
      const headingId = group.querySelector('[id$="-heading"]')?.id;
      const match = headingId ? target.querySelector(`[id="${headingId}"]`)?.closest('.search__box-item') : null;
      const additions = match ? Array.from(group.querySelectorAll('[data-search-option]')) : [group];
      additions.forEach((addition) => {
        const clone = addition.cloneNode(true);
        [clone, ...clone.querySelectorAll('[id]')].forEach((element) => {
          if (element.id && target.querySelector(`[id="${element.id}"]`)) element.id = `${element.id}-typed`;
        });
        if (match) match.querySelector('ul')?.append(clone);
        // Category shortcuts lead the list, as the server renders them.
        else if (clone.classList?.contains('ll-search-category-matches')) target.prepend(clone);
        else target.append(clone);
      });
    });
    this.cleanResults(target);
    return { source: target, count: target.querySelectorAll('[data-search-option]').length };
  }
  showCorrection(correction) {
    const notice = this.correctionElement;
    if (!notice) return;
    notice.replaceChildren();
    notice.hidden = !correction;
    if (!correction) return;
    // Merged results also contain what was typed: say "også", and offer only the typed words.
    const text = document.createElement('span');
    const term = document.createElement('strong');
    term.textContent = correction.query;
    text.append(correction.merged ? 'Viser også treff for «' : 'Viser treff for «', term, '».');
    const original = document.createElement('button');
    original.type = 'button';
    original.className = 'll-search-correction__original';
    original.textContent = `${correction.merged ? 'Søk bare etter' : 'Søk etter'} «${correction.original}»`;
    original.addEventListener('click', () => {
      this.exactQueries.add(this.queryKey(correction.original));
      this.input.focus();
      this.onChange();
    });
    notice.append(text, ' ', original);
  }
  async renderSection() {
    const query = this.getQuery();
    this.abortController?.abort();
    const requestIndex = ++this.requestIndex;
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    this.setAttribute('loading', 'true');
    this.removeAttribute('error');
    this.resultsElement.setAttribute('aria-busy', 'true');
    this.resetButton.classList.add('btn--loading');
    this.status.textContent = 'Søker …';
    try {
      const terms = window.LaserlabSearchTerms;
      const originalRequest = this.fetchResults(query, signal);
      originalRequest.catch(() => {}); // Handled below; avoids an unhandled rejection while the vocabulary loads.
      let suggestion = null;
      if (terms && !this.exactQueries.has(this.queryKey(query))) {
        const corrected = terms.correct(query, await terms.ready());
        if (corrected.changed) suggestion = corrected.query;
      }
      const suggestionRequest = suggestion ? this.fetchResults(suggestion, signal).catch(() => null) : null;
      const original = await originalRequest;
      const suggested = suggestionRequest ? await suggestionRequest : null;
      if (requestIndex !== this.requestIndex || query !== this.getQuery()) return;
      // Use the suggestion when the typed query finds nothing, or clearly less (under half as many results).
      let results = original;
      this.correction = null;
      if (suggested?.count && (!original.count || suggested.count >= original.count * 2)) {
        results = original.count ? this.mergeResults(suggested, original) : suggested;
        this.correction = { original: query, query: suggestion, merged: original.count > 0 };
      }
      this.resultsElement.innerHTML = results.source.innerHTML;
      this.setAttribute('results', 'true');
      this.showCorrection(this.correction);
      const count = this.resultsElement.querySelectorAll('[data-search-option]').length;
      this.querySelector('.search__results-all').disabled = count === 0;
      if (!count) {
        this.resultsElement.replaceChildren(this.querySelector('[data-search-empty]').content.cloneNode(true));
        this.resultsElement.querySelector('[data-search-empty-query]').textContent = query;
      }
      this.resetSelection();
      this.input.setAttribute('aria-expanded', count ? 'true' : 'false');
      const correctionStatus = this.correction ? `${this.correction.merged ? 'Viser også treff for' : 'Viser treff for'} ${this.correction.query}. ` : '';
      this.status.textContent = count ? `${correctionStatus}${count} søkeforslag. Bruk piltastene for å velge, eller Enter for alle treff.` : 'Ingen søkeforslag. Prøv et annet søkeord.';
      this.refreshDrawerFocus();
    } catch (error) {
      if (error.name === 'AbortError' || requestIndex !== this.requestIndex) return;
      this.correction = null;
      this.showCorrection(null);
      this.setAttribute('error', 'true');
      this.setAttribute('results', 'true');
      this.querySelector('.search__results-all').disabled = false;
      this.resultsElement.innerHTML = '';
      const message = document.createElement('p');
      message.className = 'll-search-error';
      message.textContent = 'Søkeforslag er midlertidig utilgjengelige. Trykk Enter eller «Se alle søkeresultater» for å søke.';
      this.resultsElement.append(message);
      this.status.textContent = message.textContent;
      this.input.setAttribute('aria-expanded', 'false');
      this.refreshDrawerFocus();
    } finally {
      if (requestIndex === this.requestIndex) {
        this.removeAttribute('loading');
        this.resultsElement.removeAttribute('aria-busy');
        this.resetButton.classList.remove('btn--loading');
      }
    }
  }
  resetSelection() {
    this.activeOption = -1;
    this.input.removeAttribute('aria-activedescendant');
    this.resultsElement?.querySelectorAll('[aria-selected="true"]').forEach((option) => option.setAttribute('aria-selected', 'false'));
  }
  onKeyDown(event) {
    if (event.key === 'Escape') {
      if (this.menuDrawer?.hasAttribute('data-search-active')) {
        event.preventDefault();
        event.stopPropagation();
      } else this.closeSuggestions();
      return;
    }
    if (!this.hasAttribute('results') || this.input.getAttribute('aria-expanded') !== 'true') return;
    const options = Array.from(this.resultsElement.querySelectorAll('[data-search-option]'));
    if (event.key === 'Enter' && this.activeOption >= 0) {
      event.preventDefault();
      options[this.activeOption]?.querySelector('a')?.click();
      return;
    }
    if (!['ArrowDown', 'ArrowUp'].includes(event.key) || !options.length) return;
    event.preventDefault();
    const next = this.activeOption === -1 ? (event.key === 'ArrowDown' ? 0 : options.length - 1) : this.activeOption + (event.key === 'ArrowDown' ? 1 : -1);
    this.resetSelection();
    this.activeOption = (next + options.length) % options.length;
    const option = options[this.activeOption];
    option.setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', option.id);
    option.scrollIntoView({ block: 'nearest' });
  }
  closeSuggestions() {
    this.input.setAttribute('aria-expanded', 'false');
    this.resetSelection();
    if (this.closest('.template-search')) document.body.classList.remove('predictive-search-open');
  }
  refreshDrawerFocus() {
    if (this.menuDrawer?.open && this.menuDrawer.hasAttribute('data-search-active')) {
      FoxTheme.a11y.removeTrapFocus();
      return;
    }
    const drawer = this.closest('search-drawer[open]');
    if (!drawer) return;
    const active = document.activeElement;
    const focused = this.contains(active) ? active : this.input;
    const start = this.input.selectionStart;
    const end = this.input.selectionEnd;
    FoxTheme.a11y.trapFocus(drawer, focused);
    if (focused === this.input && start !== null) this.input.setSelectionRange(start, end);
  }
}
if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

class SearchDrawer extends DrawerComponent {
  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted || !this.open) return;
      this.restoringHistory = true;
      this.hide();
      this.restoringHistory = false;
    }, { signal: this.abortController.signal });
  }
  handleShowTransition() {
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) return super.handleShowTransition();
    this.setAttribute('active', '');
    return Promise.resolve();
  }
  handleHideTransition() {
    if (this.restoringHistory || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.removeAttribute('active');
      return Promise.resolve();
    }
    return super.handleHideTransition();
  }
  get requiresBodyAppended() { return false; }
  get input() { return this.querySelector('input[type="search"]'); }
  get focusElement() { return this.input; }
}
if (!customElements.get('search-drawer')) customElements.define('search-drawer', SearchDrawer);
